import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface PanelDefinition {
  id: string;
  content: ReactNode;
  expandContent?: ReactNode;
  isEmpty?: boolean;
  onExpand?: () => void;
}

interface DraggablePanelGridProps {
  panels: PanelDefinition[];
  panelOrder: string[];
  onOrderChange: (newOrder: string[]) => void;
}

export default function DraggablePanelGrid({
  panels,
  panelOrder,
  onOrderChange,
}: DraggablePanelGridProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const dragCounter = useRef<Record<string, number>>({});

  // Build a map from id → visual position (CSS order) based on panelOrder
  const orderMap = useMemo(() => {
    const map = new Map<string, number>();
    panelOrder.forEach((id, index) => map.set(id, index));
    return map;
  }, [panelOrder]);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent, id: string) => {
      e.preventDefault();
      dragCounter.current[id] = (dragCounter.current[id] ?? 0) + 1;
      if (id !== draggedId) {
        setOverId(id);
      }
    },
    [draggedId],
  );

  const handleDragLeave = useCallback((_e: React.DragEvent, id: string) => {
    dragCounter.current[id] = (dragCounter.current[id] ?? 0) - 1;
    if (dragCounter.current[id] <= 0) {
      dragCounter.current[id] = 0;
      setOverId(prev => (prev === id ? null : prev));
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      dragCounter.current = {};
      setOverId(null);
      setDraggedId(null);

      if (!draggedId || draggedId === targetId) return;

      const currentOrder = [...panelOrder];
      const fromIndex = currentOrder.indexOf(draggedId);
      const toIndex = currentOrder.indexOf(targetId);
      if (fromIndex === -1 || toIndex === -1) return;

      currentOrder.splice(fromIndex, 1);
      currentOrder.splice(toIndex, 0, draggedId);
      onOrderChange(currentOrder);
    },
    [draggedId, panelOrder, onOrderChange],
  );

  const handleDragEnd = useCallback(() => {
    dragCounter.current = {};
    setDraggedId(null);
    setOverId(null);
  }, []);

  const { t } = useTranslation();

  // Render panels in their ORIGINAL (stable) order to avoid DOM moves,
  // use CSS `order` to visually reorder them. This prevents Leaflet's
  // MapContainer from being detached/reattached which causes the
  // "Map container is being reused by another instance" error.
  return (
    <>
      {panels.map(panel => {
        const isDragged = draggedId === panel.id;
        const isOver = overId === panel.id && draggedId !== panel.id;
        const cssOrder = orderMap.get(panel.id) ?? 0;

        return (
          <Grid
            key={panel.id}
            size={{ xs: 12, md: 4 }}
            sx={{ order: cssOrder, height: "30vh" }}
            onDragOver={handleDragOver}
            onDragEnter={e => handleDragEnter(e, panel.id)}
            onDragLeave={e => handleDragLeave(e, panel.id)}
            onDrop={e => handleDrop(e, panel.id)}
          >
            <Box
              sx={{
                position: "relative",
                height: "100%",
                overflow: "hidden",
                opacity: isDragged ? 0.4 : 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                outline: isOver ? "2px dashed" : "2px dashed transparent",
                outlineOffset: -2,
                outlineColor: isOver ? "primary.main" : "transparent",
                transition: "opacity 150ms, outline-color 150ms",
                "&:hover .drag-handle": { opacity: 0.6 },
                "&:hover .expand-handle": { opacity: 0.6 },
              }}
            >
              <Box
                className="drag-handle"
                draggable
                onDragStart={e => handleDragStart(e, panel.id)}
                onDragEnd={handleDragEnd}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 1100,
                  cursor: "grab",
                  opacity: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0.25,
                  borderRadius: 0.5,
                  color: "text.secondary",
                  transition: "opacity 150ms",
                  "&:hover": { opacity: 1, color: "text.primary" },
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
              {panel.onExpand && (
                <Tooltip title={t("common.expand")}>
                  <Box
                    className="expand-handle"
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      zIndex: 1100,
                      opacity: 0,
                      transition: "opacity 150ms",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <IconButton size="small" onClick={panel.onExpand} sx={{ p: 0.25 }}>
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
              {panel.isEmpty ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: 150,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("common.nothingToShow", "Nothing to show")}
                  </Typography>
                </Box>
              ) : (
                panel.content
              )}
            </Box>
          </Grid>
        );
      })}
    </>
  );
}
